import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
    Alert,
    Box,
    Button,
    ButtonBase,
    Card,
    CardContent,
    CircularProgress,
    FormControlLabel,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import axiosInstance from "../AxiosInstance";

interface Connection {
    isEnabled: boolean;
    credentialsConfigured: boolean;
}

interface EposSettingsPageProps {
    embedded?: boolean;
}

const messageFromError = (error: unknown, fallback: string) => {
    if (axios.isAxiosError(error)) {
        const data = error.response?.data as { message?: string; detail?: string } | undefined;
        return data?.message ?? data?.detail ?? fallback;
    }
    return fallback;
};

export default function EposSettingsPage({ embedded = false }: EposSettingsPageProps) {
    const [connection, setConnection] = useState<Connection | null>(null);
    const [selectedRetailer, setSelectedRetailer] = useState<"sainsburys" | "waitrose">("sainsburys");
    const selectedRetailerRef = useRef(selectedRetailer);
    const selectionVersionRef = useRef(0);
    const [enabled, setEnabled] = useState(false);
    const [userId, setUserId] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const connectionEndpoint = `/epos/${selectedRetailer}`;

    useEffect(() => {
        let active = true;
        setLoading(true);
        setSaving(false);
        setError("");
        setSuccess("");
        setConnection(null);
        setEnabled(false);
        setUserId("");
        setPassword("");

        void axiosInstance.get<Connection>(connectionEndpoint)
            .then(response => {
                if (!active) return;
                setConnection(response.data);
                setEnabled(response.data.isEnabled);
            })
            .catch(requestError => {
                if (active)
                    setError(messageFromError(requestError, "EPOS settings could not be loaded."));
            })
            .finally(() => {
                if (active)
                    setLoading(false);
            });

        return () => { active = false; };
    }, [connectionEndpoint]);

    const save = async () => {
        const retailer = selectedRetailer;
        const retailerNameForRequest = retailer === "sainsburys" ? "Sainsbury's" : "Waitrose";
        const endpoint = `/epos/${retailer}`;
        const selectionVersion = selectionVersionRef.current;
        const isCurrentSelection = () => selectedRetailerRef.current === retailer &&
            selectionVersionRef.current === selectionVersion;

        if ((userId.length > 0) !== (password.length > 0)) {
            setError(`Enter both your ${retailerNameForRequest} username and password.`);
            return;
        }

        setSaving(true);
        setError("");
        setSuccess("");
        try {
            const response = await axiosInstance.put<Connection>(endpoint, {
                isEnabled: enabled,
                userId: userId || null,
                password: password || null
            });
            if (!isCurrentSelection()) return;
            setConnection(response.data);
            setUserId("");
            setPassword("");
            setSuccess(`${retailerNameForRequest} settings saved.`);
        } catch (requestError) {
            if (isCurrentSelection())
                setError(messageFromError(requestError, "EPOS settings could not be saved."));
        } finally {
            if (isCurrentSelection())
                setSaving(false);
        }
    };

    if (loading) {
        return <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}><CircularProgress /></Box>;
    }

    return (
        <Box sx={{ maxWidth: embedded ? "none" : 960, mx: "auto", py: embedded ? 0 : { xs: 2, md: 5 }, px: embedded ? 0 : { xs: 1, sm: 2 } }}>
            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

            <Stack spacing={3}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    {([
                        { id: "sainsburys" as const, name: "Sainsbury's", background: "#fff3e0", color: "#e65100" },
                        { id: "waitrose" as const, name: "Waitrose", background: "#e8f5e9", color: "#00843d" }
                    ]).map(retailer => (
                        <Card
                            key={retailer.id}
                            variant="outlined"
                            sx={theme => {
                                const isSelected = selectedRetailer === retailer.id;
                                return {
                                    flex: 1,
                                    borderColor: isSelected ? "primary.main" : "divider",
                                    borderWidth: isSelected ? 2 : 1,
                                    transition: "border-color 160ms ease, box-shadow 160ms ease",
                                    boxShadow: isSelected ? "0 8px 24px rgba(25, 118, 210, 0.10)" : "none",
                                    ...theme.applyStyles("dark", {
                                        borderColor: isSelected ? "primary.light" : "divider",
                                        boxShadow: isSelected ? `0 0 0 1px ${theme.palette.primary.light}` : "none"
                                    })
                                };
                            }}
                        >
                            <ButtonBase
                                component="div"
                                role="button"
                                aria-label={`Select ${retailer.name}`}
                                aria-pressed={selectedRetailer === retailer.id}
                                onClick={() => {
                                    if (selectedRetailerRef.current !== retailer.id) {
                                        selectedRetailerRef.current = retailer.id;
                                        selectionVersionRef.current += 1;
                                    }
                                    setSelectedRetailer(retailer.id);
                                }}
                                sx={{ display: "block", width: "100%", textAlign: "left", borderRadius: 1 }}
                            >
                                <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, p: { xs: 2, sm: 2.5 }, "&:last-child": { pb: { xs: 2, sm: 2.5 } } }}>
                                    <Box sx={{ display: "grid", placeItems: "center", width: 52, height: 52, flexShrink: 0, borderRadius: 2, bgcolor: retailer.background, color: retailer.color }}>
                                        <StorefrontOutlinedIcon />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 700 }}>{retailer.name}</Typography>
                                    </Box>
                                </CardContent>
                            </ButtonBase>
                        </Card>
                    ))}
                </Stack>

                {connection && (
                    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3.5 }, borderRadius: 2 }}>
                        <Stack spacing={3}>
                            <Box>
                                <Typography color="text.secondary" sx={{ mt: 0.75 }}>
                                    Enter the details you use to sign in to {selectedRetailer === "sainsburys" ? "Circana Unify+" : "Waitrose Connect"}.
                                </Typography>
                            </Box>

                            {connection?.credentialsConfigured && (
                                <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ p: 2, borderRadius: 1.5, bgcolor: "action.hover" }}>
                                    <LockOutlinedIcon color="action" fontSize="small" sx={{ mt: 0.25 }} />
                                    <Typography variant="body2" color="text.secondary">
                                        {connection?.credentialsConfigured
                                            ? "Your details are already saved. Leave both fields blank to keep them, or enter new details to replace them."
                                            : ""}
                                    </Typography>
                                </Stack>)}

                            <Stack spacing={2.5}>
                                <TextField
                                    label={selectedRetailer === "sainsburys" ? "User ID" : "Username"}
                                    value={userId}
                                    onChange={(event) => setUserId(event.target.value)}
                                    autoComplete="username"
                                    fullWidth
                                />
                                <TextField
                                    label="Password"
                                    value={password}
                                    onChange={(event) => setPassword(event.target.value)}
                                    type="password"
                                    autoComplete="new-password"
                                    fullWidth
                                />
                            </Stack>

                            <Box sx={{ pt: 0.5 }}>
                                <FormControlLabel
                                    control={<Switch checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />}
                                    label={<Typography sx={{ fontWeight: 600 }}>Enabled</Typography>}
                                />
                            </Box>

                            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                <Button variant="contained" size="large" onClick={() => void save()} disabled={saving} sx={{ minWidth: 150 }}>
                                    {saving ? "Saving…" : "Save changes"}
                                </Button>
                            </Box>
                        </Stack>
                    </Paper>
                )}
            </Stack>
        </Box>
    );
}
